/* eslint-disable max-lines */
import { LoadingButton } from '@mui/lab';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Grid,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import SelectField from 'common/forms/SelectField';
import useRoles from 'common/hooks/useRoles';
import useWebSocket from 'common/hooks/useWebSocket';
import TransferResultsModal from 'common/modules/TransferResultsModal';
import { selectTransferDestinations } from 'common/utils/facilityEligibility';
import {
  TRANSFER_STATUS,
  buildTransferResultRows,
  shouldShowTransferResultsDialog,
  summarizeTransferOutcomes,
} from 'common/utils/transferOutcomes';
import { every, includes, isEmpty, map, some, uniqBy } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { selectOtherUsersLockedItemsById } from 'store/slices/lockedLineItemsSlice';
import { multipleTransferOrderToFacility } from 'store/thunks/workQueueThunks';

const TransferFacilityContext = createContext([]);

const TransferFacilityProvider = ({ children }) => {
  const [selectedLineItems, setSelectedLineItems] = useState([]);
  const { isConnected, sendMessage } = useWebSocket();
  const webSocketMessages = useSelector(({ webSocket }) => webSocket?.messages);
  const currentUserId = useSelector(({ auth }) => auth?.userId);
  const otherUsersLockedItems = useSelector((state) =>
    selectOtherUsersLockedItemsById(state, currentUserId)
  );
  const selectedLineItemsRef = useRef(selectedLineItems);
  const processedMessageCountRef = useRef(0);

  useEffect(() => {
    selectedLineItemsRef.current = selectedLineItems;
  }, [selectedLineItems]);

  const selectedLineItemIds = useMemo(
    () => map(selectedLineItems, 'lineItemId'),
    [selectedLineItems]
  );

  const orderIds = useMemo(() => {
    const selectedOrderIds = map(selectedLineItems, 'orderId').filter(Boolean);
    return Array.from(new Set(selectedOrderIds));
  }, [selectedLineItems]);

  const lineItemHasValidData = (lineItem) => !!lineItem?.lineItemId;

  useEffect(() => {
    if (!webSocketMessages || webSocketMessages.length === 0) return;

    const newMessages = webSocketMessages.slice(processedMessageCountRef.current);
    const expiredMessages = newMessages.filter((msg) => msg.event === 'line_items_expired');

    if (expiredMessages.length > 0) {
      const allExpiredLineItemIds = [];

      expiredMessages.forEach((msg) => {
        if (msg.data && Array.isArray(msg.data)) {
          msg.data.forEach((item) => {
            if (item.lineItemIds && Array.isArray(item.lineItemIds)) {
              allExpiredLineItemIds.push(...item.lineItemIds);
            } else if (item.enhancedLockDetails && Array.isArray(item.enhancedLockDetails)) {
              item.enhancedLockDetails.forEach(({ lineItemId }) => {
                if (lineItemId) allExpiredLineItemIds.push(lineItemId);
              });
            }
          });
        }
      });

      if (allExpiredLineItemIds.length > 0) {
        setSelectedLineItems((prev) =>
          prev.filter(({ lineItemId }) => !allExpiredLineItemIds.includes(lineItemId))
        );
      }
    }

    processedMessageCountRef.current = webSocketMessages.length;
  }, [webSocketMessages]);

  useEffect(() => {
    if (isEmpty(otherUsersLockedItems)) return;

    setSelectedLineItems((prev) => {
      if (isEmpty(prev)) return prev;
      return prev.filter(({ lineItemId }) => !otherUsersLockedItems.includes(lineItemId));
    });
  }, [otherUsersLockedItems]);

  const updateTransferItems = useCallback(
    ({ isSelected, lineItem }) => {
      if (!lineItemHasValidData(lineItem)) return;
      const targetLineItemId = lineItem.lineItemId;

      setSelectedLineItems((prev) => {
        if (isSelected) {
          return uniqBy([...prev, lineItem], 'lineItemId');
        }

        return prev.filter(({ lineItemId }) => lineItemId !== targetLineItemId);
      });

      if (isConnected) {
        sendMessage(isSelected ? 'lineItems:lock' : 'lineItems:unlock', {
          lineItemIds: [targetLineItemId],
          sessionId: sessionStorage.getItem('socketSessionId'),
        });
      }
    },
    [isConnected, sendMessage]
  );

  const updateTransferItemsBulk = useCallback(
    ({ isSelected, lineItems }) => {
      const validLineItems = (lineItems || []).filter(lineItemHasValidData);
      const lineItemIds = map(validLineItems, 'lineItemId');
      if (isEmpty(lineItemIds)) return;

      setSelectedLineItems((prev) => {
        if (isSelected) {
          return uniqBy([...prev, ...validLineItems], 'lineItemId');
        }

        return prev.filter(({ lineItemId }) => !lineItemIds.includes(lineItemId));
      });

      if (isConnected) {
        sendMessage(isSelected ? 'lineItems:lock' : 'lineItems:unlock', {
          lineItemIds,
          sessionId: sessionStorage.getItem('socketSessionId'),
        });
      }
    },
    [isConnected, sendMessage]
  );

  const clearTransferItems = useCallback(() => {
    const currentLineItemIds = map(selectedLineItemsRef.current, 'lineItemId');
    if (isConnected && !isEmpty(currentLineItemIds)) {
      sendMessage('lineItems:unlock', {
        lineItemIds: currentLineItemIds,
        sessionId: sessionStorage.getItem('socketSessionId'),
      });
    }

    setSelectedLineItems([]);
  }, [isConnected, sendMessage]);

  useEffect(() => {
    return () => {
      const currentSelectedLineItems = selectedLineItemsRef.current;
      const currentLineItemIds = map(currentSelectedLineItems, 'lineItemId');

      if (isConnected && !isEmpty(currentLineItemIds)) {
        sendMessage('lineItems:unlock', {
          lineItemIds: currentLineItemIds,
          sessionId: sessionStorage.getItem('socketSessionId'),
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const memoizedValue = useMemo(
    () => ({
      selectedLineItems,
      selectedLineItemIds,
      orderIds,
      updateTransferItems,
      updateTransferItemsBulk,
      clearTransferItems,
    }),
    [
      selectedLineItems,
      selectedLineItemIds,
      orderIds,
      updateTransferItems,
      updateTransferItemsBulk,
      clearTransferItems,
    ]
  );

  return (
    <TransferFacilityContext.Provider value={memoizedValue}>
      {children}
    </TransferFacilityContext.Provider>
  );
};

const useTransferFacilityContext = () => useContext(TransferFacilityContext);

const SelectAllFacilityCheckBox = ({ allLineItems, isDisabled }) => {
  const { selectedLineItemIds, updateTransferItemsBulk } = useTransferFacilityContext();
  const allLineItemIds = map(allLineItems, 'lineItemId');
  const includeSomeIds = some(allLineItemIds, (lineItemId) =>
    includes(selectedLineItemIds, lineItemId)
  );
  const allIdsChecked = every(allLineItemIds, (lineItemId) =>
    includes(selectedLineItemIds, lineItemId)
  );

  const handleClickEvent = (event) => {
    const { checked } = event.target;
    updateTransferItemsBulk({ isSelected: checked, lineItems: allLineItems });
  };

  return (
    <Tooltip
      title={isDisabled ? 'order is not in appropriate state for transfer!' : ''}
      placement='left'
      arrow
    >
      <span>
        <Checkbox
          size='small'
          onClick={handleClickEvent}
          indeterminate={!allIdsChecked && includeSomeIds}
          checked={allIdsChecked && !isEmpty(selectedLineItemIds)}
          disabled={isDisabled}
        />
      </span>
    </Tooltip>
  );
};

const FacilityCheckBox = ({ lineItem, isDisabled }) => {
  const { selectedLineItemIds, updateTransferItems } = useTransferFacilityContext();

  const onChangeHandler = (event) => {
    const isSelected = event.target.checked;
    updateTransferItems({ isSelected, lineItem });
  };
  return (
    <Tooltip
      title={isDisabled ? 'order is not in appropriate state for transfer!' : ''}
      placement='left'
      arrow
    >
      <span>
        <Checkbox
          id={lineItem.lineItemId}
          size='small'
          sx={{ py: 0 }}
          checked={includes(selectedLineItemIds, lineItem.lineItemId)}
          onChange={onChangeHandler}
          disabled={isDisabled}
        />
      </span>
    </Tooltip>
  );
};

const MultiTransferFacilityButton = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const hasAccess = useRoles();
  const { selectedLineItemIds, clearTransferItems } = useTransferFacilityContext();
  const currentTenantId = useSelector(({ auth }) => auth?.currentTenantId);
  const fulfillingPharmacies = useSelector(({ pharmacy }) => pharmacy[currentTenantId]);

  // POM-5906/5907: dispensing facilities only. No current-facility exclusion here — a bulk
  // selection spans multiple orders, so there is no single facility to exclude.
  const transferFacilities = selectTransferDestinations(fulfillingPharmacies);

  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Non-null while the post-transfer results dialog is up. Holds the rows plus the counts it needs.
  const [transferResults, setTransferResults] = useState(null);

  // Dismissing the results dialog is what completes the cleanup — but only when something actually
  // moved. A fully blocked batch keeps the selection so the operator can retry another facility.
  const handleResultsClose = () => {
    const didTransfer = (transferResults?.totalTransferred ?? 0) > 0;
    setTransferResults(null);

    if (didTransfer) {
      clearTransferItems();
      onSuccess();
    }
  };

  const handleOpenModal = () => setShowModal(true);

  const handleCloseModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setShowModal(false);
    }
  };

  const handleTransferInitiation = ({ fulfillingPharmacy: toFacility }) => {
    setIsLoading(true);
    dispatch(
      multipleTransferOrderToFacility({
        lineItemIds: selectedLineItemIds,
        fulfillingPharmacyId: toFacility.fulfillingPharmacyId,
      })
    )
      .then((response) => {
        // POM-5945: the old check was `if (isTransferred)` on the response ARRAY — always truthy —
        // and clearTransferItems()/onSuccess() ran from an unconditional .finally(), so a blocked or
        // failed bulk transfer still wiped the operator's selection and refreshed the queue.
        const { status, totalTransferred, totalRequested, reasonSummary } =
          summarizeTransferOutcomes(response);

        // Request failed; handleError owns the messaging. Keep the selection so it can be retried.
        if (status === TRANSFER_STATUS.ERROR) return;

        // A partial result, or a fully blocked multi-order batch, needs per-order attribution that a
        // snackbar cannot carry. The selection clear and queue refresh move to the dialog's dismissal
        // so the operator can read the failures while the queue still shows them.
        if (shouldShowTransferResultsDialog({ status, totalRequested })) {
          setShowModal(false);
          setTransferResults({
            rows: buildTransferResultRows(response.outcomes),
            totalRequested,
            totalTransferred,
            destinationLabel: `${toFacility.name} - ${toFacility.state}`,
          });

          return;
        }

        if (status === TRANSFER_STATUS.NONE) {
          enqueueSnackbar(
            reasonSummary
              ? `No orders transferred: ${reasonSummary}`
              : 'No orders were eligible for transfer.',
            { variant: 'error', autoHideDuration: 4000 }
          );

          // Dialog stays open and the selection is preserved — nothing moved.
          return;
        }

        // Unchanged all-success UX. PARTIAL no longer reaches here — it always routes to the
        // results dialog above, since a partial result means at least two orders were requested.
        enqueueSnackbar(`Order Transferred to ${toFacility.name} - ${toFacility.state}`, {
          variant: 'success',
        });

        setShowModal(false);
        clearTransferItems();
        onSuccess();
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      {transferResults && (
        <TransferResultsModal
          open
          onClose={handleResultsClose}
          rows={transferResults.rows}
          totalRequested={transferResults.totalRequested}
          totalTransferred={transferResults.totalTransferred}
          destinationLabel={transferResults.destinationLabel}
        />
      )}

      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        aria-describedby='transferOrderHeader-header'
        fullWidth
      >
        <Grid container sx={{ px: 3, py: 2 }} justifyContent='space-between' alignItems='center'>
          <Typography variant='h6' component='h2' id='transferOrderHeader-header'>
            Transfer Order To Facility
          </Typography>
        </Grid>
        <DialogContent>
          <Form
            onSubmit={handleTransferInitiation}
            render={({ handleSubmit, invalid, values }) => (
              <form onSubmit={handleSubmit} noValidate>
                <SelectField
                  id='Transfer-facility-dropdown'
                  label='Select Facility To Initiate Transfer'
                  name='fulfillingPharmacy'
                  required
                >
                  {map(transferFacilities, (facility) => (
                    <MenuItem value={facility} key={facility.fulfillingPharmacyId}>
                      {facility.name} - {facility.state}
                    </MenuItem>
                  ))}
                </SelectField>

                {values.fulfillingPharmacy && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant='body2' sx={{ mb: 1 }}>
                      You are about to transfer selected line item(s) to{' '}
                      <strong>
                        {values.fulfillingPharmacy.name} - {values.fulfillingPharmacy.state}
                      </strong>
                      .
                    </Typography>
                    <Typography variant='body2' component='div'>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        <li>
                          If the order contains only one line item, the entire order will be
                          transferred to the selected facility.
                        </li>
                        <li>
                          If the order contains multiple line items and you select one or more
                          specific line items, the selected item(s) will be split into a new order
                          and transferred.
                        </li>
                      </ul>
                    </Typography>
                    <Typography variant='body2' sx={{ mt: 1 }}>
                      Do you want to continue?
                    </Typography>
                  </Box>
                )}

                <Grid container justifyContent='flex-end' sx={{ mt: 3 }}>
                  <Button
                    variant='outlined'
                    color='secondary'
                    onClick={handleCloseModal}
                    sx={{ mr: 2 }}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    variant='contained'
                    type='submit'
                    disabled={invalid}
                    loading={isLoading}
                  >
                    Yes, Transfer
                  </LoadingButton>
                </Grid>
              </form>
            )}
          />
        </DialogContent>
      </Dialog>
      <Badge color='error' badgeContent={selectedLineItemIds.length} sx={{ mx: 1 }}>
        <Button
          size='small'
          color='primary'
          variant='contained'
          onClick={handleOpenModal}
          disabled={isEmpty(selectedLineItemIds) || !hasAccess.transferFacility}
        >
          Transfer Facility
        </Button>
      </Badge>
    </>
  );
};

export {
  FacilityCheckBox,
  MultiTransferFacilityButton,
  SelectAllFacilityCheckBox,
  TransferFacilityProvider,
  useTransferFacilityContext,
};

MultiTransferFacilityButton.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};

TransferFacilityProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

SelectAllFacilityCheckBox.propTypes = {
  allLineItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string.isRequired,
      orderId: PropTypes.string,
      itemName: PropTypes.string,
      rxNumber: PropTypes.string,
    })
  ).isRequired,
  isDisabled: PropTypes.bool.isRequired,
};

FacilityCheckBox.propTypes = {
  lineItem: PropTypes.shape({
    lineItemId: PropTypes.string.isRequired,
    orderId: PropTypes.string,
    itemName: PropTypes.string,
    rxNumber: PropTypes.string,
  }).isRequired,
  isDisabled: PropTypes.bool.isRequired,
};
